
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Share, Twitter, Facebook, Linkedin, Copy, Check, ExternalLink, Image, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge as BadgeType } from '@/utils/gamification';
import BadgeCard from './BadgeCard';
import { LeaderboardEntry } from './leaderboard/LeaderboardTypes';
import analytics from '@/utils/analytics';

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: {
    type: 'badge' | 'achievement' | 'progress';
    title: string;
    description: string;
    badge?: BadgeType;
    points?: number;
    streak?: number;
    leaderboardEntry?: LeaderboardEntry;
  };
}

const ShareModal: React.FC<ShareModalProps> = ({ open, onOpenChange, content }) => {
  const [copied, setCopied] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  
  const shareText = `${content.title} - ${content.description}${content.points ? ` | ${content.points} points earned` : ''}${content.streak ? ` | ${content.streak} day streak` : ''} #HormonyHealth`;
  const shareUrl = `https://hormony.health/share?ref=app`;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    
    analytics.trackUserAction('share_content', { 
      method: 'copy_link',
      contentType: content.type 
    });
    
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleSharePlatform = (platform: string) => {
    let shareUrl = '';
    
    switch(platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`;
        break;
      default:
        return;
    }
    
    analytics.trackUserAction('share_content', { 
      method: platform,
      contentType: content.type 
    });
    
    window.open(shareUrl, '_blank');
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share size={18} className="text-hormony-coral" />
            Share Your {content.type === 'badge' ? 'Badge' : content.type === 'achievement' ? 'Achievement' : 'Progress'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col space-y-4 py-4">
          {content.badge && (
            <div className="flex justify-center mb-2">
              <BadgeCard badge={content.badge} size="lg" showProgress={false} />
            </div>
          )}
          
          <h3 className="text-xl font-semibold text-center">{content.title}</h3>
          <p className="text-center text-muted-foreground">{content.description}</p>
          
          {(content.points || content.streak) && (
            <div className="flex justify-center gap-8 bg-hormony-beige/10 p-3 rounded-lg">
              {content.points && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Points</p>
                  <p className="font-medium text-hormony-coral">{content.points}</p>
                </div>
              )}
              {content.streak && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Streak</p>
                  <p className="font-medium text-hormony-coral">{content.streak} days</p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center space-x-2">
            <Input 
              value={`${shareText} ${shareUrl}`}
              readOnly
              className="flex-1"
            />
            <Button variant="outline" size="icon" onClick={handleCopyLink}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </div>
          
          <div className="flex justify-center space-x-4">
            <Button 
              variant="outline" 
              size="icon" 
              className="rounded-full w-10 h-10 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 text-[#1DA1F2]"
              onClick={() => handleSharePlatform('twitter')}
            >
              <Twitter size={18} />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              className="rounded-full w-10 h-10 bg-[#4267B2]/10 hover:bg-[#4267B2]/20 text-[#4267B2]"
              onClick={() => handleSharePlatform('facebook')}
            >
              <Facebook size={18} />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              className="rounded-full w-10 h-10 bg-[#0077B5]/10 hover:bg-[#0077B5]/20 text-[#0077B5]"
              onClick={() => handleSharePlatform('linkedin')}
            >
              <Linkedin size={18} />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              className="rounded-full w-10 h-10 bg-gray-100 hover:bg-gray-200"
              onClick={() => setShowQRCode(!showQRCode)}
            >
              {showQRCode ? <X size={18} /> : <ExternalLink size={18} />}
            </Button>
          </div>
          
          {showQRCode && (
            <div className="flex flex-col items-center">
              <div className="bg-white p-2 rounded-lg border">
                <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                  <Image size={24} className="text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">QR Code</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Scan this QR code to share your achievement
              </p>
            </div>
          )}
        </div>
        
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="coral" onClick={handleCopyLink}>
            <Copy size={16} className="mr-2" />
            Copy Link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
